/**
 * Shaping for the Tracking page's History tab (get_ops_history RPC in
 * supabase/migrations/20261027140000_dashboard_reports.sql). The SQL defines
 * every number; this module arranges them and names the records behind each
 * one (get_owner_records kinds mail_events, deleted_rfqs and rfqs), so the
 * page can list them. Owner rules: volume is mailbox threads, turnaround is
 * clock time for RFQs arriving Mon-Fri 09:00-18:00 Singapore.
 */

import { singaporeToday, type DrillSpec } from '../analytics/owner/ownerData';
import { FAILURE_LABELS, shiftDays, type FailureKind } from './opsHealth';

export interface OpsDayCounts {
  rfq_emails_read: number;
  could_not_read: Record<FailureKind | 'total', number>;
  deleted_in_app: number;
  auto_archived: number;
  threads_received: number;
  replied_system: number;
  replied_manual: number;
  no_email_reply: number;
  office_rfqs: number;
  answered_office: number;
  median_clock_minutes: number | null;
  within_1h: number;
  within_4h: number;
  pct_within_1h: number | null;
  pct_within_4h: number | null;
}

export interface OpsDay extends OpsDayCounts {
  day: string;
}

export interface OpsHistory {
  generated_at: string;
  range: { from: string; to: string };
  /** The mailbox is rebuilt daily ~03:30 SGT; thread, reply and speed figures after this are incomplete. */
  mailbox_scanned_through: string | null;
  daily: OpsDay[];
  totals: OpsDayCounts;
}

export const HISTORY_RANGES = [
  { days: 7, label: '7 days' },
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
] as const;

export type HistoryDays = (typeof HISTORY_RANGES)[number]['days'];

/** The last `days` Singapore calendar days including today. */
export function historyRange(days: number, today: string): { from: string; to: string } {
  return { from: shiftDays(today, -(days - 1)), to: today };
}

/**
 * Is a day's mailbox data (threads, replies, speed) still incomplete? The
 * nightly scan covers mail up to the moment it ran, so the day it ran on and
 * every later day are partial. With no scan on record every day is.
 */
export function isMailboxPartial(day: string, scannedThrough: string | null): boolean {
  return !scannedThrough || day >= singaporeToday(new Date(scannedThrough));
}

/** 'Mon 27 Oct' for a Singapore date. */
export function dayLabel(day: string, withWeekday = false): string {
  const d = new Date(`${day}T00:00:00Z`);
  const month = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][d.getUTCMonth()];
  const weekday = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d.getUTCDay()];
  return `${withWeekday ? `${weekday} ` : ''}${d.getUTCDate()} ${month}`;
}

/** 'on Mon 27 Oct' for one day, '21-27 Oct' style text for a range. */
export function periodText(from: string, to: string): string {
  return from === to ? `on ${dayLabel(from, true)}` : `${dayLabel(from)} - ${dayLabel(to)}`;
}

export type HistoryMetric =
  | 'read'
  | 'could_not_read'
  | FailureKind
  | 'deleted_in_app'
  | 'auto_archived'
  | 'threads'
  | 'replied_system'
  | 'replied_manual'
  | 'no_email_reply'
  | 'answered_office';

/**
 * The records behind one History number, over a day (from = to) or the whole
 * range. Each drill's total equals the number it was opened from (the SQL
 * shares the definitions).
 */
export function historyDrill(metric: HistoryMetric, from: string, to: string): DrillSpec {
  const when = periodText(from, to);
  const mail = (outcome: string, title: string, note: string): DrillSpec => ({ title: `${title}, ${when}`, kind: 'mail_events', from, to, filter: { outcome }, note });
  switch (metric) {
    case 'read':
      return mail('read', 'Emails read as an RFQ', 'One row per email the system turned into an RFQ, including RFQs deleted in the app since. A deleted RFQ is not linked.');
    case 'could_not_read':
      return mail('could_not_read', 'Emails and Excel fills that need a look', 'Emails the system could not read, emails that read like an RFQ but gave no items, and Excel fills that failed or stuck.');
    case 'rfq_parse_failed':
    case 'looks_like_rfq_no_items':
    case 'excel_job_partial':
    case 'excel_job_stuck':
      return mail(metric, FAILURE_LABELS[metric], 'An email that stays unread is checked again on every pass; Attempts counts those checks.');
    case 'deleted_in_app':
      return {
        title: `RFQs deleted by a person, ${when}`,
        kind: 'deleted_rfqs',
        from,
        to,
        filter: { auto_archived: false },
        note: 'RFQs deleted in the app, by the day they were deleted. They no longer exist, so they are not linked.',
      };
    case 'auto_archived':
      return {
        title: `RFQs archived automatically, ${when}`,
        kind: 'deleted_rfqs',
        from,
        to,
        filter: { auto_archived: true },
        note: 'The nightly 03:00 job removes RFQs still NEW after 3 working days. They no longer exist, so they are not linked.',
      };
    case 'threads':
      return { title: `RFQs received in the mailbox, ${when}`, kind: 'rfqs', from, to, note: 'Every RFQ thread in the sales mailbox, by the time the first email arrived (Singapore time).' };
    case 'replied_system':
      return { title: `RFQs answered from the system, ${when}`, kind: 'rfqs', from, to, filter: { channel: 'system' } };
    case 'replied_manual':
      return { title: `RFQs answered by hand, ${when}`, kind: 'rfqs', from, to, filter: { channel: 'manual' }, note: 'First reply sent from Outlook rather than the system.' };
    case 'no_email_reply':
      return {
        title: `RFQs with no email reply, ${when}`,
        kind: 'rfqs',
        from,
        to,
        filter: { answered: false },
        note: 'No reply from DBR found in the mailbox. Many were answered in a customer portal (Wrist, Seven Seas, ARC) or by phone.',
      };
    case 'answered_office':
    default:
      return {
        title: `Office-hours RFQs answered, ${when}`,
        kind: 'rfqs',
        from,
        to,
        filter: { office: true, answered: true },
        note: 'RFQs that arrived Mon-Fri 09:00-18:00 Singapore time and got an email reply; the wait is clock time from arrival to the first reply.',
      };
  }
}

export interface HistoryChartRow {
  day: string;
  label: string;
  partial: boolean;
  read: number;
  couldNotRead: number;
  deletedInApp: number;
  autoArchived: number;
  system: number;
  manual: number;
  noReply: number;
  answered: number;
  median: number | null;
  /** Shares only where at least `minAnswered` office-hours RFQs were answered; small days read as noise. */
  within1h: number | null;
  within4h: number | null;
}

/** One chart row per day, with mailbox-partial days flagged. */
export function historyChartRows(h: Pick<OpsHistory, 'daily' | 'mailbox_scanned_through'>, minAnswered = 3): HistoryChartRow[] {
  return h.daily.map((d) => {
    const enough = d.answered_office >= minAnswered;
    return {
      day: d.day,
      label: dayLabel(d.day),
      partial: isMailboxPartial(d.day, h.mailbox_scanned_through),
      read: d.rfq_emails_read,
      couldNotRead: d.could_not_read.total,
      deletedInApp: d.deleted_in_app,
      autoArchived: d.auto_archived,
      system: d.replied_system,
      manual: d.replied_manual,
      noReply: d.no_email_reply,
      answered: d.answered_office,
      median: d.median_clock_minutes,
      within1h: enough ? d.pct_within_1h : null,
      within4h: enough ? d.pct_within_4h : null,
    };
  });
}

/** Share of mailbox RFQ threads answered by email, and the system's share of those replies. */
export function replyShares(t: Pick<OpsDayCounts, 'threads_received' | 'replied_system' | 'replied_manual'>): { answered: number | null; viaSystem: number | null } {
  const replied = t.replied_system + t.replied_manual;
  const pct = (part: number, whole: number) => (whole ? Math.round((1000 * part) / whole) / 10 : null);
  return { answered: pct(replied, t.threads_received), viaSystem: pct(t.replied_system, replied) };
}
